import { externalNetworkClient } from '@terra/shared-rest-clients-external-network';
import { internalNetworkClient } from '@terra/shared-rest-clients-internal-network';
import type { ExternalNetworkContract } from '@terra/shared-contracts-external-network';
import type { InternalNetworkContract } from '@terra/shared-contracts-internal-network';
import type {
  UpdateExternalNetworkDto,
  UpsertSurfaceProfileDto,
} from '@terra/shared-contracts-external-network';
import type { UpdateInternalNetworkDto } from '@terra/shared-contracts-internal-network';
import { tenantAwareApiWrapper, withOrchestratorClient } from './api-core';

export const surfaceSettingsApi = {
  getInternalNetwork: (networkId: string) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.getById>({
      endpointFn: (headers) =>
        internalNetworkClient.getById({
          params: { networkId },
          ...withOrchestratorClient(headers),
        }),
    }),

  updateInternalNetwork: ({
    networkId,
    body,
  }: {
    networkId: string;
    body: UpdateInternalNetworkDto;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.update>({
      endpointFn: (headers) =>
        internalNetworkClient.update({
          params: { networkId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteInternalNetwork: (networkId: string) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.delete>({
      endpointFn: (headers) =>
        internalNetworkClient.delete({
          params: { networkId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listInternalNetworkEnrollments: (networkId: string) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.listEnrollments>({
      endpointFn: (headers) =>
        internalNetworkClient.listEnrollments({
          params: { networkId },
          ...withOrchestratorClient(headers),
        }),
    }),

  listInternalNetworkCredentials: (networkId: string) =>
    tenantAwareApiWrapper<typeof InternalNetworkContract.listCredentials>({
      endpointFn: (headers) =>
        internalNetworkClient.listCredentials({
          params: { networkId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getExternalNetwork: (surfaceId: string) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getById>({
      endpointFn: (headers) =>
        externalNetworkClient.getById({
          params: { surfaceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  updateExternalNetwork: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: UpdateExternalNetworkDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.update>({
      endpointFn: (headers) =>
        externalNetworkClient.update({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteExternalNetwork: (surfaceId: string) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.delete>({
      endpointFn: (headers) =>
        externalNetworkClient.delete({
          params: { surfaceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getExternalNetworkProfile: (surfaceId: string) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.getProfile>({
      endpointFn: (headers) =>
        externalNetworkClient.getProfile({
          params: { surfaceId },
          ...withOrchestratorClient(headers),
        }),
    }),

  upsertExternalNetworkProfile: ({
    surfaceId,
    body,
  }: {
    surfaceId: string;
    body: UpsertSurfaceProfileDto;
  }) =>
    tenantAwareApiWrapper<typeof ExternalNetworkContract.upsertProfile>({
      endpointFn: (headers) =>
        externalNetworkClient.upsertProfile({
          params: { surfaceId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),
};
